/**
 * Citation Display Helper
 * Generates properly formatted APA citations for display in cards and lists
 */

import { generateReference, formatNameForReference, formatAuthorsForReference, looksLikeOrganizationName, formatMixedNameListForReference } from './citationGenerator';
import { mapBackendToFormFields, getFrontendSourceType, getFrontendCategory } from './citationFieldMapper';
import { isSafeHttpUrl, normalizeUrlProtocol } from './referenceOutputUtils';

function escapeHtmlAttr(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;');
}

/**
 * Allow only <em> and safe http(s) <a> in reference HTML shown on cards.
 * Rejects javascript:/data: and other non-http schemes.
 */
export function sanitizeCitationHtml(html) {
	if (!html) return '';
	let s = String(html);
	s = s.replace(/<a\b([^>]*)>([\s\S]*?)<\/a>/gi, (_full, attrs, inner) => {
		const hrefMatch = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs || '');
		const href = hrefMatch ? hrefMatch[1] || hrefMatch[2] || hrefMatch[3] || '' : '';
		if (!isSafeHttpUrl(href)) return inner;
		const url = normalizeUrlProtocol(href);
		return `<a href="${escapeHtmlAttr(url)}" target="_blank" rel="noopener noreferrer">${inner}</a>`;
	});
	// Keep <em>/<a> (open or close); strip every other tag.
	return s.replace(/<(?!\/?(?:em|a)\b)[^>]*>/gi, '');
}

/**
 * Generate a formatted citation for display from backend citation data
 * @param {object} citation - Citation object from backend
 * @param {boolean} stripHtml - Whether to remove HTML tags (default: true for card display)
 * @returns {string} - Formatted APA citation
 */
export function generateCitationForDisplay(citation, stripHtml = true) {
	if (!citation) return '';

	try {
		const frontendCategory = getFrontendCategory(citation) || citation.category;
		const { subcategory, sourceType } = citation;

		// Get frontend source type for citation generator
		const backendSubcategory = subcategory || sourceType;
		const frontendSourceType = getFrontendSourceType(backendSubcategory, citation);

		// Transform backend data to form fields for citation generator
		const formFields = mapBackendToFormFields(citation);

		// Generate the full reference citation
		if (frontendCategory && frontendSourceType) {
			let fullCitation = generateReference(frontendCategory, frontendSourceType, formFields);

			// Remove HTML tags for card display if requested
			if (stripHtml) {
				fullCitation = fullCitation.replace(/<[^>]*>/g, '');
			}

			return fullCitation;
		}
	} catch (error) {
		console.error('Error generating citation for display:', error, citation);
	}

	// Fallback to basic citation format if generation fails
	return generateFallbackCitation(citation);
}

/**
 * Generate a simple fallback citation when proper generation fails
 * @param {object} citation - Citation object
 * @returns {string} - Basic citation format
 */
function generateFallbackCitation(citation) {
	const formatAuthors = (authorList) => {
		if (!authorList || authorList.length === 0) return 'Unknown Author';
		if (authorList.length === 1) return authorList[0];
		if (authorList.length === 2) return `${authorList[0]} & ${authorList[1]}`;
		const allButLast = authorList.slice(0, -1).join(', ');
		return `${allButLast}, & ${authorList[authorList.length - 1]}`;
	};

	const authors = formatAuthors(citation.authors || []);
	const year = citation.year || citation.date?.split(',')[0] || 'n.d.';
	const title = citation.title ||
		citation.chapterTitle ||
		citation.articleTitle ||
		citation.excerptOrPostTitle ||
		'Untitled';

	return `${authors} (${year}). ${title}.`;
}

/**
 * Get formatted author names for citation card header (reference-style names).
 * Rules:
 * - 1 author: "LastName, F. M."
 * - 2 authors: "LastName1, F. M., & LastName2, F. M."
 * - 3+ authors: "LastName1, F. M. et al."
 * 
 * @param {object} citation - Citation object
 * @returns {string} - Formatted author names for card header
 */
export function getPrimaryAuthor(citation) {
	if (!citation) return 'Unknown Author';

	if (citation.subcategory === 'Personal Communication' && citation.communicatorName?.trim()) {
		return citation.communicatorName.trim();
	}

	if (citation.subcategory === 'TV Episode' && citation.writers?.length) {
		const valid = citation.writers.map((w) => w.name || w).filter((x) => x && String(x).trim());
		if (valid.length) return formatAuthorsForReference(valid);
	}

	if (citation.subcategory === 'Podcast Episode' && citation.contributors?.length) {
		const contributors = citation.contributors.map((c) => c.name || c);
		const roles =
			typeof citation.context === 'string'
				? citation.context.split(',').map((r) => r.trim())
				: [];
		const hosts = contributors.filter((_, i) => (roles[i] || '').toLowerCase() === 'host');
		const list = hosts.length > 0 ? hosts : contributors.slice(0, 1);
		if (list.length) return formatAuthorsForReference(list);
	}

	if (citation.subcategory === 'Webinar/Lecture' && citation.presenters?.length) {
		const list = citation.presenters.map((p) => p.name || p).filter((x) => x && String(x).trim());
		if (list.length) return formatAuthorsForReference(list);
	}

	if (citation.subcategory === 'Song or Music Track' && citation.artists?.length) {
		const list = citation.artists.map((a) => a.name || a).filter((x) => x && String(x).trim());
		if (list.length) return formatMixedNameListForReference(list);
	}

	if (citation.subcategory === 'Course Handout' && citation.presenters?.length) {
		const list = citation.presenters.map((p) => p.name || p).filter((x) => x && String(x).trim());
		if (list.length) return formatMixedNameListForReference(list);
	}

	if (
		(citation.subcategory === 'Conference Presentation' ||
			citation.subcategory === 'Conference Session') &&
		citation.presenters?.length
	) {
		const list = citation.presenters.map((p) => p.name || p).filter((x) => x && String(x).trim());
		const roles =
			typeof citation.context === 'string'
				? citation.context.split(',').map((r) => r.trim())
				: Array.isArray(citation.context)
					? citation.context
					: [];
		const segments = list.map((name, i) => {
			const role = roles[i] || '';
			if (looksLikeOrganizationName(name, role) || role === 'Organization') return name.trim();
			const formatted = formatNameForReference(name);
			return role ? `${formatted} (${role})` : formatted;
		});
		if (segments.length === 1) return segments[0];
		if (segments.length === 2) return `${segments[0]}, & ${segments[1]}`;
		return `${segments.slice(0, -1).join(', ')}, & ${segments[segments.length - 1]}`;
	}

	if (citation.subcategory === 'Radio Broadcast' && citation.contributors?.length) {
		const list = citation.contributors.map((c) => c.name || c).filter((x) => x && String(x).trim());
		const roles =
			typeof citation.context === 'string'
				? citation.context.split(',').map((r) => r.trim())
				: Array.isArray(citation.context)
					? citation.context
					: [];
		const segments = list.map((name, i) => {
			const role = roles[i] || '';
			if (looksLikeOrganizationName(name, role)) return name.trim();
			const formatted = formatNameForReference(name);
			return role && !/organization\s*\(no role/i.test(role) ? `${formatted} (${role})` : formatted;
		});
		if (segments.length === 1) return segments[0];
		if (segments.length === 2) return `${segments[0]}, & ${segments[1]}`;
		return `${segments.slice(0, -1).join(', ')}, & ${segments[segments.length - 1]}`;
	}

	if (citation.subcategory === 'Film/Movie' && citation.creators?.length) {
		const list = citation.creators.map((c) => c.name || c).filter((x) => x && String(x).trim());
		const roles =
			typeof citation.context === 'string'
				? citation.context.split(',').map((r) => r.trim())
				: [];
		const segments = list.map((name, i) => {
			const role = roles[i] || '';
			if (looksLikeOrganizationName(name, role)) return name.trim();
			const formatted = formatNameForReference(name);
			return role && !/organization\s*\(no role/i.test(role) ? `${formatted} (${role})` : formatted;
		});
		if (segments.length === 1) return segments[0];
		if (segments.length === 2) return `${segments[0]}, & ${segments[1]}`;
		return `${segments.slice(0, -1).join(', ')}, & ${segments[segments.length - 1]}`;
	}

	// Check if this is an organization citation - display organization name as-is
	if (citation.organization) {
		return citation.organization;
	}

	const { authors } = citation;

	if (!authors || authors.length === 0) {
		return 'Unknown Author';
	}

	const validAuthors = authors.filter((a) => a && a.trim());

	if (validAuthors.length === 0) return 'Unknown Author';

	// Format each author in reference style (LastName, F. M.)
	const formattedAuthors = validAuthors.map((author) => formatNameForReference(author));

	if (formattedAuthors.length === 1) {
		return formattedAuthors[0];
	} else if (formattedAuthors.length === 2) {
		return `${formattedAuthors[0]}, & ${formattedAuthors[1]}`;
	} else {
		// For 3+ authors, show first author with et al.
		return `${formattedAuthors[0]} et al.`;
	}
}

